"use client";
import { ApolloLink, HttpLink } from "@apollo/client";
import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
} from "@apollo/client-integration-nextjs";
import typePolicies from "./typepolicies";
import { generatePersistedQueryIdsFromManifest } from "@apollo/persisted-query-lists";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import manifest from "../redesign-queries.json" with { type: "json" };
import { useLocale } from "next-intl";

const makeClient = (locale) => () => {
  const uri =
    typeof window !== "undefined"
      ? process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT
      : process.env.UNCHAINED_ENDPOINT;

  const httpLink = new HttpLink({
    uri,
    fetchOptions: {
      timeout: 10000,
    },
    credentials: "same-origin",
    fetch,
    headers: process.env.PERSISTED_QUERIES_KEY
      ? {
          "X-Unlock-API": process.env.PERSISTED_QUERIES_KEY,
          "Accept-Language": locale,
        }
      : {
          "Accept-Language": locale,
        },
    includeExtensions: true,
  });

  const isDevMode =
    process.env.NODE_ENV !== "production" && !process.env.NEXT_PUBLIC_FORCE_PQ;

  const persistedQueryLink = createPersistedQueryLink({
    ...generatePersistedQueryIdsFromManifest({
      loadManifest: () => {
        const operations = Object.entries(manifest).map(([name, id]) => ({
          id,
          name,
        }));
        return { operations };
      },
    }),
    useGETForHashedQueries: true,
    disable: () => {
      return isDevMode;
    },
  });

  const cache = new InMemoryCache({
    typePolicies,
  });

  const client = new ApolloClient({
    cache,
    link:
      (process.env.PERSISTED_QUERIES_KEY ?? isDevMode)
        ? httpLink
        : ApolloLink.from([persistedQueryLink, httpLink]),
  });

  return client;
};

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  const locale = useLocale();
  return (
    <ApolloNextAppProvider makeClient={makeClient(locale)}>
      {children}
    </ApolloNextAppProvider>
  );
}
